// Rückfragen und Meldungen im App-Stil statt window.confirm/alert (Übergabe-Redesign, Freigabe 17.10.2026):
// - melde(text): kurze Meldung als Toast, mehrteilige (mit Zeilenumbruch) als Hinweis-Dialog mit „OK“
// - bestaetige({ nachricht, label }): Promise<boolean>; erste Zeile = Titel, weitere Absätze darunter,
//   „Das kann NICHT rückgängig gemacht werden.“ steht rot.
// `dialoge` muss der Aufrufer einmal rendern.
import { useCallback, useState, type ReactNode } from 'react';
import { Trash, type Icon } from '@phosphor-icons/react';
import { BestaetigenDialog, SchwebenderToast, useToastPill } from '../immobilien/ui';
import { MeldungDialog } from './MeldungDialog';

export const NICHT_RUECKGAENGIG = 'Das kann NICHT rückgängig gemacht werden.';

export interface Rueckfrage {
  /** Absätze durch „\n\n“ getrennt; der erste wird Titel */
  nachricht: string;
  label: string;
  abbrechenLabel?: string;
  icon?: Icon;
  /** rot (Standard) oder Akzent */
  gefahr?: boolean;
}

export function useAppMeldungen(mobil: boolean) {
  const [toast, zeigeToast] = useToastPill();
  const [meldung, setMeldung] = useState<{ text: string; fehler: boolean } | null>(null);
  const [frage, setFrage] = useState<(Rueckfrage & { antwort: (ok: boolean) => void }) | null>(null);

  const melde = useCallback((text: string, fehler = false) => {
    if (text.includes('\n')) setMeldung({ text, fehler }); else zeigeToast(text, fehler);
  }, [zeigeToast]);

  const bestaetige = useCallback((r: Rueckfrage) => new Promise<boolean>((antwort) => {
    setFrage({ ...r, antwort: (ok) => { setFrage(null); antwort(ok); } });
  }), []);

  let frageDialog: ReactNode = null;
  if (frage) {
    const [titel, ...absaetze] = frage.nachricht.split('\n\n');
    frageDialog = (
      <BestaetigenDialog titel={titel} text="" icon={frage.icon ?? Trash} gefahr={frage.gefahr ?? true} bestaetigenLabel={frage.label}
        abbrechenLabel={frage.abbrechenLabel}
        detail={absaetze.length > 0 ? (
          <div className="flex flex-col" style={{ gap: 8 }}>
            {absaetze.map((a, i) => (
              <p key={i} className="m-0 whitespace-pre-line" style={a === NICHT_RUECKGAENGIG ? { color: 'var(--bad)', fontWeight: 800 } : { lineHeight: 1.45 }}>{a}</p>
            ))}
          </div>
        ) : undefined}
        onAbbrechen={() => frage.antwort(false)} onBestaetigen={() => frage.antwort(true)} />
    );
  }

  const dialoge = (
    <>
      {frageDialog}
      {meldung && <MeldungDialog text={meldung.text} fehler={meldung.fehler} onSchliessen={() => setMeldung(null)} />}
      <SchwebenderToast toast={toast} mobil={mobil} />
    </>
  );

  return { melde, bestaetige, dialoge };
}
